import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Collapse,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Tab,
  Tabs,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
} from '@mui/material';
import {
  KeyboardArrowDown as ExpandIcon,
  KeyboardArrowUp as CollapseIcon,
} from '@mui/icons-material';
import DateInputWithArrows from '../components/DateInputWithArrows';
import PersonnelHoursHistoryList from '../components/PersonnelHoursHistoryList';
import HoursEntryEditDot from '../components/HoursEntryEditDot';
import ResponsivePageSection from '../components/responsive/ResponsivePageSection';
import { formatDateEuropean, toLocalDateString } from '../utils/dateUtils';
import {
  getPersonnel,
  getProjects,
  getPersonnelHoursReportByPersonnel,
  getPersonnelHoursReportByProject,
} from '../services/api';
import { useNotification } from '../context/NotificationContext';
import { useTranslation } from 'react-i18next';
import { formatHoursEntryMeta } from '../utils/hoursEntryDisplay';

function getDefaultDateRange() {
  const now = new Date();
  const from = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-01`;
  const to = toLocalDateString(now);
  return { from, to };
}

function formatPersonLabel(person) {
  const parts = [person.firstName, person.lastName].filter(Boolean).join(' ');
  if (person.nickname) {
    return parts ? `${parts} (${person.nickname})` : person.nickname;
  }
  return parts || `#${person.id}`;
}

function formatHours(hours) {
  const n = Number(hours);
  if (Number.isNaN(n)) return '0';
  return (Math.round(n * 10) / 10).toFixed(1).replace(/\.0$/, '');
}

function WorkerHoursReport() {
  const { t } = useTranslation();
  const { showNotification } = useNotification();
  const defaultRange = useMemo(() => getDefaultDateRange(), []);

  const [tab, setTab] = useState(0);
  const [from, setFrom] = useState(defaultRange.from);
  const [to, setTo] = useState(defaultRange.to);
  const [projects, setProjects] = useState([]);
  const [personnelList, setPersonnelList] = useState([]);
  const [selectedPersonnelId, setSelectedPersonnelId] = useState('');
  const [selectedProjectId, setSelectedProjectId] = useState('');
  const [workerProjectFilter, setWorkerProjectFilter] = useState('');
  const [loading, setLoading] = useState(false);
  const [workerReport, setWorkerReport] = useState(null);
  const [projectReport, setProjectReport] = useState(null);
  const [expandedDateKeys, setExpandedDateKeys] = useState({});
  const [expandedWorkerKeys, setExpandedWorkerKeys] = useState({});
  const [expandedHistoryKeys, setExpandedHistoryKeys] = useState({});

  useEffect(() => {
    const load = async () => {
      try {
        const [projectsRes, personnelRes] = await Promise.all([
          getProjects(),
          getPersonnel(),
        ]);
        setProjects(projectsRes.data || []);
        setPersonnelList(personnelRes.data || []);
      } catch (error) {
        console.error(error);
        showNotification(t('notifications.errorLoadingProjects', { defaultValue: 'Error loading data' }), 'error');
      }
    };
    load();
  }, [showNotification, t]);

  const sortedPersonnel = useMemo(
    () => [...personnelList].sort((a, b) => formatPersonLabel(a).localeCompare(formatPersonLabel(b))),
    [personnelList]
  );

  const validateRange = () => {
    if (!from || !to || from > to) {
      showNotification(t('workerHoursReport.invalidDateRange'), 'warning');
      return false;
    }
    return true;
  };

  const runWorkerReport = async () => {
    if (!selectedPersonnelId) {
      showNotification(t('workerHoursReport.selectWorker'), 'warning');
      return;
    }
    if (!validateRange()) return;

    setLoading(true);
    setWorkerReport(null);
    setExpandedDateKeys({});
    setExpandedHistoryKeys({});
    try {
      const params = {
        personnelId: selectedPersonnelId,
        from,
        to,
      };
      if (workerProjectFilter) {
        params.projectId = workerProjectFilter;
      }
      const res = await getPersonnelHoursReportByPersonnel(params);
      setWorkerReport(res.data);
    } catch (error) {
      console.error(error);
      showNotification(error.response?.data?.error || error.message, 'error');
    } finally {
      setLoading(false);
    }
  };

  const runProjectReport = async () => {
    if (!selectedProjectId) {
      showNotification(t('workerHoursReport.selectProject'), 'warning');
      return;
    }
    if (!validateRange()) return;

    setLoading(true);
    setProjectReport(null);
    setExpandedWorkerKeys({});
    setExpandedHistoryKeys({});
    try {
      const res = await getPersonnelHoursReportByProject({
        projectId: selectedProjectId,
        from,
        to,
      });
      setProjectReport(res.data);
    } catch (error) {
      console.error(error);
      showNotification(error.response?.data?.error || error.message, 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleRun = () => {
    if (tab === 0) {
      runWorkerReport();
    } else {
      runProjectReport();
    }
  };

  const toggleDateExpand = (dateKey) => {
    setExpandedDateKeys((prev) => ({ ...prev, [dateKey]: !prev[dateKey] }));
  };

  const toggleWorkerExpand = (workerKey) => {
    setExpandedWorkerKeys((prev) => ({ ...prev, [workerKey]: !prev[workerKey] }));
  };

  const toggleHistoryExpand = (entryId) => {
    setExpandedHistoryKeys((prev) => ({ ...prev, [entryId]: !prev[entryId] }));
  };

  const workerKey = (w) => (w.type === 'person' ? `person-${w.personnelId}` : `company-${w.companyId}`);

  return (
    <ResponsivePageSection>
      <Typography variant="h4" gutterBottom>
        {t('workerHoursReport.title')}
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        {t('workerHoursReport.subtitle')}
      </Typography>

      <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 2 }}>
        <Tab label={t('workerHoursReport.tabByWorker')} />
        <Tab label={t('workerHoursReport.tabByProject')} />
      </Tabs>

      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Grid container spacing={2} alignItems="flex-end">
            <Grid item xs={12} sm={6} md={3}>
              <DateInputWithArrows
                label={t('workerHoursReport.fromDate')}
                value={from}
                onChange={(e) => setFrom(e.target.value)}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <DateInputWithArrows
                label={t('workerHoursReport.toDate')}
                value={to}
                onChange={(e) => setTo(e.target.value)}
              />
            </Grid>

            {tab === 0 ? (
              <>
                <Grid item xs={12} sm={6} md={3}>
                  <FormControl fullWidth>
                    <InputLabel id="worker-select-label">{t('workerHoursReport.worker')}</InputLabel>
                    <Select
                      labelId="worker-select-label"
                      label={t('workerHoursReport.worker')}
                      value={selectedPersonnelId}
                      onChange={(e) => setSelectedPersonnelId(e.target.value)}
                    >
                      {sortedPersonnel.map((p) => (
                        <MenuItem key={p.id} value={String(p.id)}>
                          {formatPersonLabel(p)}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <FormControl fullWidth>
                    <InputLabel id="worker-project-filter-label">{t('workerHoursReport.project')}</InputLabel>
                    <Select
                      labelId="worker-project-filter-label"
                      label={t('workerHoursReport.project')}
                      value={workerProjectFilter}
                      onChange={(e) => setWorkerProjectFilter(e.target.value)}
                    >
                      <MenuItem value="">{t('workerHoursReport.allProjects')}</MenuItem>
                      {projects.map((p) => (
                        <MenuItem key={p.id} value={String(p.id)}>
                          {p.name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
              </>
            ) : (
              <Grid item xs={12} sm={6} md={3}>
                <FormControl fullWidth>
                  <InputLabel id="project-select-label">{t('workerHoursReport.project')}</InputLabel>
                  <Select
                    labelId="project-select-label"
                    label={t('workerHoursReport.project')}
                    value={selectedProjectId}
                    onChange={(e) => setSelectedProjectId(e.target.value)}
                  >
                    {projects.map((p) => (
                      <MenuItem key={p.id} value={String(p.id)}>
                        {p.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            )}

            <Grid item xs={12} sm={6} md={tab === 0 ? 12 : 3}>
              <Button
                variant="contained"
                onClick={handleRun}
                disabled={loading}
                fullWidth
              >
                {loading ? <CircularProgress size={24} color="inherit" /> : t('workerHoursReport.runReport')}
              </Button>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {tab === 0 && workerReport && (
        <Box>
          <Box sx={{ mb: 2, display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
            <Chip
              label={`${t('workerHoursReport.totalHours')}: ${formatHours(workerReport.totalHours)}`}
              color="primary"
            />
            <Typography variant="body2" color="text.secondary">
              {formatPersonLabel(workerReport.personnel)}
              {' · '}
              {formatDateEuropean(workerReport.from)}
              {' – '}
              {formatDateEuropean(workerReport.to)}
            </Typography>
          </Box>

          {workerReport.byProject?.length === 0 && workerReport.byDate?.length === 0 ? (
            <Alert severity="info">{t('workerHoursReport.noData')}</Alert>
          ) : (
            <>
              {workerReport.byProject?.length > 0 && (
                <Box sx={{ mb: 3 }}>
                  <Typography variant="h6" gutterBottom>
                    {t('workerHoursReport.byProject')}
                  </Typography>
                  <TableContainer component={Paper}>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>{t('workerHoursReport.projectName')}</TableCell>
                          <TableCell align="right">{t('workerHoursReport.hours')}</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {workerReport.byProject.map((row) => (
                          <TableRow key={row.projectId}>
                            <TableCell>{row.projectName}</TableCell>
                            <TableCell align="right">{formatHours(row.hours)}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Box>
              )}

              {workerReport.byDate?.length > 0 && (
                <Box>
                  <Typography variant="h6" gutterBottom>
                    {t('workerHoursReport.dailyBreakdown')}
                  </Typography>
                  <TableContainer component={Paper}>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell width={48} />
                          <TableCell>{t('workerHoursReport.date')}</TableCell>
                          <TableCell align="right">{t('workerHoursReport.hours')}</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {workerReport.byDate.map((day) => (
                          <React.Fragment key={day.date}>
                            <TableRow hover>
                              <TableCell>
                                <IconButton
                                  size="small"
                                  onClick={() => toggleDateExpand(day.date)}
                                  aria-label={expandedDateKeys[day.date]
                                    ? t('workerHoursReport.collapseDaily')
                                    : t('workerHoursReport.expandDaily')}
                                >
                                  {expandedDateKeys[day.date] ? <CollapseIcon /> : <ExpandIcon />}
                                </IconButton>
                              </TableCell>
                              <TableCell>
                                <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.75 }}>
                                  {formatDateEuropean(day.date)}
                                  <HoursEntryEditDot hasEdits={day.hasEdits} />
                                </Box>
                              </TableCell>
                              <TableCell align="right">{formatHours(day.hours)}</TableCell>
                            </TableRow>
                            <TableRow>
                              <TableCell colSpan={3} sx={{ py: 0, borderBottom: expandedDateKeys[day.date] ? undefined : 0 }}>
                                <Collapse in={expandedDateKeys[day.date]} timeout="auto" unmountOnExit>
                                  <Box sx={{ py: 1, pl: 2 }}>
                                    <Table size="small">
                                      <TableHead>
                                        <TableRow>
                                          <TableCell width={48} />
                                          <TableCell>{t('workerHoursReport.projectName')}</TableCell>
                                          <TableCell align="right">{t('workerHoursReport.hours')}</TableCell>
                                        </TableRow>
                                      </TableHead>
                                      <TableBody>
                                        {(day.projects || []).map((p) => (
                                          <React.Fragment key={p.projectId}>
                                            <TableRow>
                                              <TableCell>
                                                {p.entryId ? (
                                                  <IconButton
                                                    size="small"
                                                    onClick={() => toggleHistoryExpand(p.entryId)}
                                                    aria-label={expandedHistoryKeys[p.entryId]
                                                      ? t('workerHoursReport.collapseHistory')
                                                      : t('workerHoursReport.expandHistory')}
                                                  >
                                                    {expandedHistoryKeys[p.entryId] ? <CollapseIcon /> : <ExpandIcon />}
                                                  </IconButton>
                                                ) : null}
                                              </TableCell>
                                              <TableCell>
                                                <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.75 }}>
                                                  {p.projectName}
                                                  <HoursEntryEditDot hasEdits={p.hasEdits} />
                                                </Box>
                                                {formatHoursEntryMeta(p, t).map((line) => (
                                                  <Typography key={line} variant="caption" color="text.secondary" display="block">
                                                    {line}
                                                  </Typography>
                                                ))}
                                              </TableCell>
                                              <TableCell align="right">{formatHours(p.hours)}</TableCell>
                                            </TableRow>
                                            {p.entryId ? (
                                              <TableRow>
                                                <TableCell colSpan={3} sx={{ py: 0, borderBottom: expandedHistoryKeys[p.entryId] ? undefined : 0 }}>
                                                  <Collapse in={expandedHistoryKeys[p.entryId]} timeout="auto" unmountOnExit>
                                                    <Box sx={{ py: 1, pl: 1 }}>
                                                      <Typography variant="caption" color="text.secondary" display="block" gutterBottom>
                                                        {t('workerHoursReport.changeHistory')}
                                                      </Typography>
                                                      <PersonnelHoursHistoryList entryId={p.entryId} compact />
                                                    </Box>
                                                  </Collapse>
                                                </TableCell>
                                              </TableRow>
                                            ) : null}
                                          </React.Fragment>
                                        ))}
                                      </TableBody>
                                    </Table>
                                  </Box>
                                </Collapse>
                              </TableCell>
                            </TableRow>
                          </React.Fragment>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Box>
              )}
            </>
          )}
        </Box>
      )}

      {tab === 1 && projectReport && (
        <Box>
          <Box sx={{ mb: 2, display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
            <Chip
              label={`${t('workerHoursReport.totalHours')}: ${formatHours(projectReport.totalHours)}`}
              color="primary"
            />
            <Typography variant="body2" color="text.secondary">
              {projectReport.project?.name}
              {' · '}
              {formatDateEuropean(projectReport.from)}
              {' – '}
              {formatDateEuropean(projectReport.to)}
            </Typography>
          </Box>

          {!projectReport.workers?.length ? (
            <Alert severity="info">{t('workerHoursReport.noData')}</Alert>
          ) : (
            <TableContainer component={Paper}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell width={48} />
                    <TableCell>{t('workerHoursReport.name')}</TableCell>
                    <TableCell>{t('workerHoursReport.type')}</TableCell>
                    <TableCell align="right">{t('workerHoursReport.hours')}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {projectReport.workers.map((w) => {
                    const key = workerKey(w);
                    return (
                      <React.Fragment key={key}>
                        <TableRow hover>
                          <TableCell>
                            <IconButton
                              size="small"
                              onClick={() => toggleWorkerExpand(key)}
                              disabled={!w.byDate?.length}
                              aria-label={expandedWorkerKeys[key]
                                ? t('workerHoursReport.collapseDaily')
                                : t('workerHoursReport.expandDaily')}
                            >
                              {expandedWorkerKeys[key] ? <CollapseIcon /> : <ExpandIcon />}
                            </IconButton>
                          </TableCell>
                          <TableCell>
                            <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.75 }}>
                              {w.name}
                              <HoursEntryEditDot hasEdits={w.hasEdits} />
                            </Box>
                          </TableCell>
                          <TableCell>
                            {w.type === 'subcontractor'
                              ? t('workerHoursReport.typeSubcontractor')
                              : t('workerHoursReport.typePerson')}
                          </TableCell>
                          <TableCell align="right">{formatHours(w.hours)}</TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell colSpan={4} sx={{ py: 0, borderBottom: expandedWorkerKeys[key] ? undefined : 0 }}>
                            <Collapse in={expandedWorkerKeys[key]} timeout="auto" unmountOnExit>
                              <Box sx={{ py: 1, pl: 2 }}>
                                <Table size="small">
                                  <TableHead>
                                    <TableRow>
                                      <TableCell width={48} />
                                      <TableCell>{t('workerHoursReport.date')}</TableCell>
                                      <TableCell align="right">{t('workerHoursReport.hours')}</TableCell>
                                    </TableRow>
                                  </TableHead>
                                  <TableBody>
                                    {(w.byDate || []).map((d) => (
                                      <React.Fragment key={d.date}>
                                        <TableRow>
                                          <TableCell>
                                            {d.entryId ? (
                                              <IconButton
                                                size="small"
                                                onClick={() => toggleHistoryExpand(d.entryId)}
                                                aria-label={expandedHistoryKeys[d.entryId]
                                                  ? t('workerHoursReport.collapseHistory')
                                                  : t('workerHoursReport.expandHistory')}
                                              >
                                                {expandedHistoryKeys[d.entryId] ? <CollapseIcon /> : <ExpandIcon />}
                                              </IconButton>
                                            ) : null}
                                          </TableCell>
                                          <TableCell>
                                            <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.75 }}>
                                              {formatDateEuropean(d.date)}
                                              <HoursEntryEditDot hasEdits={d.hasEdits} />
                                            </Box>
                                            {formatHoursEntryMeta(d, t).map((line) => (
                                              <Typography key={line} variant="caption" color="text.secondary" display="block">
                                                {line}
                                              </Typography>
                                            ))}
                                          </TableCell>
                                          <TableCell align="right">{formatHours(d.hours)}</TableCell>
                                        </TableRow>
                                        {d.entryId ? (
                                          <TableRow>
                                            <TableCell colSpan={3} sx={{ py: 0, borderBottom: expandedHistoryKeys[d.entryId] ? undefined : 0 }}>
                                              <Collapse in={expandedHistoryKeys[d.entryId]} timeout="auto" unmountOnExit>
                                                <Box sx={{ py: 1, pl: 1 }}>
                                                  <Typography variant="caption" color="text.secondary" display="block" gutterBottom>
                                                    {t('workerHoursReport.changeHistory')}
                                                  </Typography>
                                                  <PersonnelHoursHistoryList entryId={d.entryId} compact />
                                                </Box>
                                              </Collapse>
                                            </TableCell>
                                          </TableRow>
                                        ) : null}
                                      </React.Fragment>
                                    ))}
                                  </TableBody>
                                </Table>
                              </Box>
                            </Collapse>
                          </TableCell>
                        </TableRow>
                      </React.Fragment>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Box>
      )}
    </ResponsivePageSection>
  );
}

export default WorkerHoursReport;
